'use client';

import { useEffect } from 'react';
import PageBuilder from '@/components/layout/PageBuilder';
import { useVisualEditing } from '@/hooks/useVisualEditing';
import { PageBlock } from '@/types/directus-schema';
import { Button } from '@/components/ui/button';
import { Pencil } from 'lucide-react';
import { useNavigation } from '@/context/NavigationContext';
import { setAttr } from '@directus/visual-editing';

interface PageClientProps {
	sections: PageBlock[];
	pageId?: string;
	isPreview?: boolean;
	displayTopbar?: boolean;
	displayMainnav?: boolean;
}

interface VisualEditingOptions {
	customClass?: string;
	onSaved?: () => void;
}

export default function PageClient({
	sections,
	pageId,
	isPreview,
	displayTopbar = true,
	displayMainnav = true,
}: PageClientProps) {
	const { isVisualEditingEnabled, apply } = useVisualEditing();
	const { setDisplayTopbar, setDisplayMainnav } = useNavigation();

	useEffect(() => {
		setDisplayTopbar(displayTopbar);
		setDisplayMainnav(displayMainnav);
	}, [displayTopbar, displayMainnav, setDisplayTopbar, setDisplayMainnav]);

	useEffect(() => {
		if (isVisualEditingEnabled) {
			apply({
				onSaved: () => {
					// window.location.reload() is required instead of router.refresh()
					// because router.refresh() reuses Next.js Data Cache and won't
					// re-fetch server component data from Directus
					window.location.reload();
				},
			} as VisualEditingOptions);
		}
	}, [isVisualEditingEnabled, apply]);

	return (
		<div className="relative">
			{isPreview && (
				<div
					title="Preview Mode Active"
					className="fixed top-0 inset-x-0 z-[100] h-1.5 bg-green-500 shadow-[0_0_8px_2px_rgba(34,197,94,0.6)] pointer-events-none"
				/>
			)}
			<PageBuilder sections={sections} />
			{isVisualEditingEnabled && pageId && (
				<div className="fixed z-50 w-full bottom-4 inset-x-0 p-4 flex justify-center items-center gap-2">
					{/* If you're not using the visual editor it's safe to remove this element. Just a helper to let editors add edit / add new blocks to a page. */}
					<Button
						id="visual-editing-button"
						variant="secondary"
						className="visual-editing-button-class"
						data-directus={setAttr({
							collection: 'pages',
							item: pageId,
							fields: ['blocks', 'meta_m2a_button'],
							mode: 'modal',
						})}
					>
						<Pencil className="size-4 mr-2" />
						Edit All Blocks
					</Button>
				</div>
			)}
			<style jsx global>{`
				/* Safe to remove this if you're not using the visual editor. */
				.directus-visual-editing-overlay.visual-editing-button-class .directus-visual-editing-edit-button {
					position: absolute;
					inset: 0;
					width: 100%;
					height: 100%;
					transform: none;
					background: transparent;
				}
			`}</style>
		</div>
	);
}
